import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Language resources with embedded translations
const resources = {
  en: {
    translation: {
      "header": {
        "home": "Home",
        "services": "Services",
        "about": "About Us",
        "contact": "Contact",
        "login": "Login",
        "register": "Register",
        "profile": "Profile",
        "cart": "Cart",
        "wishlist": "Wishlist",
        "orders": "Orders",
        "logout": "Logout",
        "search": "Search services...",
        "searchResults": "Search Results",
        "viewAll": "View All"
      },
      "home": {
        "heroTitle": "Spiritual Services at Your Doorstep",
        "heroSubtitle": "Book authentic puja and spiritual ceremonies with experienced priests",
        "servicesTitle": "Our Curated Pooja Offerings",
        "servicesSubtitle": "Choose from our comprehensive range of traditional ceremonies and rituals",
        "carousel": {
          "slide1": {
            "title": "Sacred Rituals, Simplified",
            "subtitle": "Bringing Vedic Rituals Closer to You",
            "ctaText": "Learn More"
          },
          "slide2": {
            "title": "Premium Services",
            "subtitle": "Tailored solutions for your auspicious events",
            "ctaText": "Learn More"
          },
          "slide3": {
            "title": "Team with Excellence",
            "subtitle": "Connect with the divine teams",
            "ctaText": "Learn More"
          }
        },
        "services": {
          "shivaPooja": {
            "title": "Shiva Pujan",
            "description": "Sacred worship of Lord Shiva with Abhishekam, mantra chanting, and offering prayers for spiritual purification and blessings."
          },
          "deviPooja": {
            "title": "Devi Pujan",
            "description": "Divine worship of Goddess Shakti with sacred rituals, flower offerings, and prayers for prosperity, protection, and feminine power."
          },
          "navgrahPooja": {
            "title": "Nav Grah Pujan",
            "description": "Powerful ceremony to appease the nine planets (Navagraha) for removing planetary doshas and bringing harmony in life."
          },
          "vishist_sanskar": {
            "title": "Vishist Sanskar",
            "description": "Complete lifecycle ceremonies including naming, first feeding, sacred thread ceremony and traditional Hindu wedding rituals."
          },
          "shanti_samridhi": {
            "title": "Shanti and Samridhi Pujan",
            "description": "Sacred ceremonies for peace, prosperity and spiritual well-being including home entry and devotional rituals."
          },
          "dosh_nivaran": {
            "title": "Dosh Nivaran Pujan",
            "description": "Remedial ceremonies to neutralize astrological doshas and negative planetary influences for life harmony."
          },
          "shradh_karm": {
            "title": "Shradh Karm",
            "description": "Sacred ancestral rituals and Shradh ceremonies for spiritual liberation and blessings of departed souls."
          },
          "ramayanSunderKand": {
            "title": "Ramayan & Sunder Kand",
            "description": "Sacred recitation of Ramayana and Sundar Kand path for removing obstacles, bringing courage and divine blessings."
          },
          "custom_solutions": {
            "title": "Custom Spiritual Solutions",
            "description": "Personalized spiritual ceremonies and custom rituals tailored to your specific spiritual needs and requirements."
          }
        }
      },
      "language": {
        "english": "English",
        "hindi": "हिंदी",
        "changeLanguage": "Change Language"
      },
      "providerUnderReview": {
        "pageTitle": "Account Under Review",
        "welcomeText": "Welcome, {{name}} Ji!",
        "statusBadge": "Under Review",
        "mainTitle": "Your Provider Account is Being Reviewed",
        "mainDescription": "Thank you for registering as a service provider. Our team is currently reviewing your application to ensure the quality and authenticity of services on our platform.",
        "reviewInfo": {
          "timeline": "Review typically takes 1-2 business days",
          "notification": "You'll receive email notification upon approval",
          "limitedAccess": "Limited features available during review"
        },
        "timeline": {
          "registration": {
            "title": "Registration Submitted",
            "description": "Your application has been received"
          },
          "verification": {
            "title": "Document Verification",
            "description": "Our team is verifying your documents"
          },
          "activation": {
            "title": "Account Activation",
            "description": "You'll receive full access after approval"
          }
        },
        "actionCards": {
          "completeProfile": {
            "title": "Complete Your Profile",
            "description": "Make sure all your information is accurate and complete. Upload required documents to speed up the review process.",
            "button": "Update Profile"
          },
          "whatsNext": {
            "title": "What's Next?",
            "steps": {
              "step1": "Complete profile information",
              "step2": "Upload required documents (Aadhar, PAN, etc.)",
              "step3": "Wait for admin approval",
              "step4": "Access full provider dashboard"
            }
          }
        },
        "restrictedAccess": {
          "title": "Restricted Access",
          "description": "While your account is under review, you have limited access to the platform. The following features will be available after approval:",
          "features": {
            "serviceManagement": "Service Management",
            "calendarManagement": "Calendar Management",
            "bookingManagement": "Booking Management",
            "dashboardAnalytics": "Dashboard Analytics"
          }
        },
        "support": {
          "title": "Need Help?",
          "description": "If you have questions about the review process or need assistance, our support team is here to help.",
          "callSupport": "📞 Call Support"
        },
        "logout": "🚪 Logout"
      },
      "aboutUs": {
        "pageTitle": "About Us",
        "heroTitle": "Your Trusted Spiritual Partner",
        "heroSubtitle": "Connecting Hearts with Divine Traditions",
        "heroDescription": "We bring authentic spiritual services and traditional ceremonies to your doorstep with experienced priests and complete ritual arrangements.",
        "ourStoryTitle": "Our Story",
        "ourStoryText": "Founded with a deep reverence for spiritual traditions, we bridge the gap between ancient wisdom and modern convenience. Our mission is to make authentic spiritual services accessible to everyone, preserving the sanctity of traditional ceremonies while adapting to contemporary lifestyles.",
        "missionTitle": "Our Mission",
        "missionText": "To preserve and share the rich heritage of spiritual traditions through authentic ceremonies, experienced priests, and personalized spiritual guidance for every family.",
        "trustStatsTitle": "Trusted by Thousands",
        "stats": {
          "ceremonies": "1000+ Ceremonies",
          "families": "500+ Happy Families",
          "priests": "50+ Expert Priests",
          "years": "5+ Years Experience"
        },
        "trustFeaturesTitle": "Why Choose Us",
        "trustFeatures": {
          "expertPriests": "Expert Priests",
          "authenticRituals": "Authentic Rituals",
          "satisfaction": "Satisfaction Guarantee",
          "support": "24/7 Support"
        },
        "teamTitle": "Our Spiritual Team",
        "teamSubtitle": "Meet our experienced priests and spiritual guides",
        "servicesTitle": "Our Services Include",
        "cta": "Book Your Spiritual Service Today"
      },
      "contactUs": {
        "pageTitle": "Contact Us",
        "heroTitle": "Contact Us",
        "heroSubtitle": "We'd love to hear from you. Get in touch with us for any questions, feedback, or support.",
        "getTouchTitle": "Get in Touch",
        "getTouchSubtitle": "Have questions about our services? Need support? We're here to help!",
        "email": "Email",
        "emailAddress": "communication@antaryatra.org",
        "emailResponse": "We'll respond within 24 hours",
        "followUs": "Follow Us",
        "sendMessageTitle": "Send us a Message",
        "formSubtitle": "Fill out the form below and we'll get back to you as soon as possible.",
        "successTitle": "Message Sent Successfully!",
        "successMessage": "Thank you for contacting us. We'll get back to you within 24 hours.",
        "form": {
          "fullName": "Full Name",
          "fullNamePlaceholder": "Enter your full name",
          "phoneNumber": "Phone Number",
          "phoneNumberNote": "(10 digits)",
          "phoneNumberPlaceholder": "Enter 10-digit phone number",
          "emailAddress": "Email Address",
          "emailAddressPlaceholder": "Enter your email address",
          "enquiryType": "Enquiry Type",
          "message": "Message",
          "messagePlaceholder": "Tell us how we can help you...",
          "charactersCount": "characters",
          "sendingMessage": "Sending Message...",
          "sendMessage": "Send Message"
        },
        "enquiryTypes": {
          "general": "General",
          "serviceInquiry": "Service Inquiry",
          "technicalSupport": "Technical Support",
          "partnership": "Partnership",
          "feedback": "Feedback",
          "complaint": "Complaint",
          "other": "Other"
        },
        "validation": {
          "nameRequired": "Name is required",
          "nameMinLength": "Name must be at least 2 characters long",
          "nameMaxLength": "Name must be less than 50 characters",
          "nameOnlyLetters": "Name can only contain letters and spaces",
          "emailRequired": "Email is required",
          "emailInvalid": "Please enter a valid email address",
          "phoneRequired": "Phone number is required",
          "phoneExact10Digits": "Phone number must be exactly 10 digits",
          "phoneOnlyDigits": "Phone number can only contain digits",
          "messageRequired": "Message is required",
          "messageMinLength": "Message must be at least 10 characters long",
          "messageMaxLength": "Message must be less than 250 characters"
        }
      }
    }
  },
  hi: {
    translation: {
      "header": {
        "home": "होम",
        "services": "सेवाएं",
        "about": "हमारे बारे में",
        "contact": "संपर्क",
        "login": "लॉगिन",
        "register": "रजिस्टर",
        "profile": "प्रोफाइल",
        "cart": "कार्ट",
        "wishlist": "विशलिस्ट",
        "orders": "ऑर्डर",
        "logout": "लॉगआउट",
        "search": "सेवाएं खोजें...",
        "searchResults": "खोज परिणाम",
        "viewAll": "सभी देखें"
      },
      "home": {
        "heroTitle": "आपके द्वार पर आध्यात्मिक सेवाएं",
        "heroSubtitle": "अनुभवी पंडितों के साथ प्रामाणिक पूजा और आध्यात्मिक समारोह बुक करें",
        "servicesTitle": "हमारी आध्यात्मिक सेवाएं",
        "servicesSubtitle": "पारंपरिक समारोहों और अनुष्ठानों की हमारी व्यापक श्रृंखला से चुनें",
        "carousel": {
          "slide1": {
            "title": "पवित्र रीति-रिवाज, सरल बनाए गए",
            "subtitle": "वैदिक रीति-रिवाज आपके करीब लाना",
            "ctaText": "और जानें"
          },
          "slide2": {
            "title": "प्रीमियम सेवाएं",
            "subtitle": "आपके शुभ कार्यक्रमों के लिए अनुकूलित समाधान",
            "ctaText": "और जानें"
          },
          "slide3": {
            "title": "उत्कृष्टता के साथ टीम",
            "subtitle": "दिव्य टीमों से जुड़ें",
            "ctaText": "और जानें"
          }
        },
        "services": {
          "shivaPooja": {
            "title": "शिव पूजन",
            "description": "भगवान शिव की पवित्र पूजा अभिषेकम, मंत्र जाप और आध्यात्मिक शुद्धिकरण और आशीर्वाद के लिए प्रार्थना के साथ।"
          },
          "deviPooja": {
            "title": "देवी पूजन",
            "description": "देवी शक्ति की दिव्य पूजा पवित्र रीति-रिवाज, फूलों की पेशकश और समृद्धि, सुरक्षा और स्त्री शक्ति के लिए प्रार्थना के साथ।"
          },
          "navgrahPooja": {
            "title": "नवग्रह पूजन",
            "description": "नौ ग्रहों (नवग्रह) को प्रसन्न करने के लिए शक्तिशाली समारोह ग्रहों के दोष हटाने और जीवन में सामंजस्य लाने के लिए।"
          },
          "vishist_sanskar": {
            "title": "विशिष्ट संस्कार",
            "description": "संपूर्ण जीवन चक्र समारोह जिसमें नामकरण, अन्नप्राशन, यज्ञोपवीत और पारंपरिक हिंदू विवाह संस्कार शामिल हैं।"
          },
          "shanti_samridhi": {
            "title": "शांति और समृद्धि पूजन",
            "description": "शांति, समृद्धि और आध्यात्मिक कल्याण के लिए पवित्र समारोह जिसमें गृह प्रवेश और भक्ति अनुष्ठान शामिल हैं।"
          },
          "dosh_nivaran": {
            "title": "दोष निवारण पूजन",
            "description": "ज्योतिषीय दोषों और नकारात्मक ग्रहीय प्रभावों को निष्क्रिय करने के लिए उपचारात्मक समारोह जीवन में सामंजस्य के लिए।"
          },
          "shradh_karm": {
            "title": "श्राद्ध कर्म",
            "description": "पूर्वजों की आत्मा की शांति और मुक्ति के लिए पवित्र पितृ अनुष्ठान और श्राद्ध समारोह।"
          },
          "ramayanSunderKand": {
            "title": "रामायण और सुंदर कांड",
            "description": "बाधाओं को दूर करने, साहस लाने और दिव्य आशीर्वाद के लिए रामायण और सुंदर कांड पाठ का पवित्र पठन।"
          },
          "custom_solutions": {
            "title": "कस्टम आध्यात्मिक समाधान",
            "description": "आपकी विशिष्ट आध्यात्मिक आवश्यकताओं और अपेक्षाओं के अनुरूप व्यक्तिगत आध्यात्मिक समारोह और कस्टम अनुष्ठान।"
          }
        }
      },
      "language": {
        "english": "English",
        "hindi": "हिंदी",
        "changeLanguage": "भाषा बदलें"
      },
      "providerUnderReview": {
        "pageTitle": "खाता समीक्षाधीन",
        "welcomeText": "स्वागत है, {{name}} जी!",
        "statusBadge": "समीक्षाधीन",
        "mainTitle": "आपका प्रदाता खाता समीक्षा में है",
        "mainDescription": "सेवा प्रदाता के रूप में पंजीकरण के लिए धन्यवाद। हमारी टीम वर्तमान में आपके आवेदन की समीक्षा कर रही है ताकि हमारे प्लेटफॉर्म पर सेवाओं की गुणवत्ता और प्रामाणिकता सुनिश्चित की जा सके।",
        "reviewInfo": {
          "timeline": "समीक्षा आमतौर पर 1-2 व्यावसायिक दिन लेती है",
          "notification": "अनुमोदन पर आपको ईमेल सूचना प्राप्त होगी",
          "limitedAccess": "समीक्षा के दौरान सीमित सुविधाएं उपलब्ध हैं"
        },
        "timeline": {
          "registration": {
            "title": "पंजीकरण प्रस्तुत",
            "description": "आपका आवेदन प्राप्त हो गया है"
          },
          "verification": {
            "title": "दस्तावेज़ सत्यापन",
            "description": "हमारी टीम आपके दस्तावेज़ों का सत्यापन कर रही है"
          },
          "activation": {
            "title": "खाता सक्रियण",
            "description": "अनुमोदन के बाद आपको पूर्ण पहुंच मिलेगी"
          }
        },
        "actionCards": {
          "completeProfile": {
            "title": "अपनी प्रोफाइल पूरी करें",
            "description": "सुनिश्चित करें कि आपकी सभी जानकारी सटीक और पूर्ण है। समीक्षा प्रक्रिया को तेज़ करने के लिए आवश्यक दस्तावेज़ अपलोड करें।",
            "button": "प्रोफाइल अपडेट करें"
          },
          "whatsNext": {
            "title": "आगे क्या?",
            "steps": {
              "step1": "प्रोफाइल की जानकारी पूरी करें",
              "step2": "आवश्यक दस्तावेज़ अपलोड करें (आधार, पैन, आदि)",
              "step3": "प्रशासक की स्वीकृति का इंतज़ार करें",
              "step4": "पूर्ण प्रदाता डैशबोर्ड तक पहुंच प्राप्त करें"
            }
          }
        },
        "restrictedAccess": {
          "title": "प्रतिबंधित पहुंच",
          "description": "जबकि आपका खाता समीक्षाधीन है, आपके पास प्लेटफॉर्म तक सीमित पहुंच है। निम्नलिखित सुविधाएं अनुमोदन के बाद उपलब्ध होंगी:",
          "features": {
            "serviceManagement": "सेवा प्रबंधन",
            "calendarManagement": "कैलेंडर प्रबंधन",
            "bookingManagement": "बुकिंग प्रबंधन",
            "dashboardAnalytics": "डैशबोर्ड एनालिटिक्स"
          }
        },
        "support": {
          "title": "मदद चाहिए?",
          "description": "यदि आपके पास समीक्षा प्रक्रिया के बारे में प्रश्न हैं या सहायता की आवश्यकता है, तो हमारी सहायता टीम आपकी मदद के लिए यहां है।",
          "callSupport": "📞 सहायता कॉल करें"
        },
        "logout": "🚪 लॉगआउट"
      },
      "contactUs": {
        "pageTitle": "संपर्क करें",
        "heroTitle": "संपर्क करें",
        "heroSubtitle": "हम आपसे सुनना पसंद करेंगे। किसी भी प्रश्न, फीडबैक या सहायता के लिए हमसे संपर्क करें।",
        "getTouchTitle": "संपर्क में रहें",
        "getTouchSubtitle": "हमारी सेवाओं के बारे में प्रश्न हैं? सहायता चाहिए? हम यहां मदद के लिए हैं!",
        "email": "ईमेल",
        "emailAddress": "communication@antaryatra.org",
        "emailResponse": "हम 24 घंटे के अंदर जवाब देंगे",
        "followUs": "हमें फॉलो करें",
        "sendMessageTitle": "हमें एक संदेश भेजें",
        "formSubtitle": "नीचे दिए गए फॉर्म को भरें और हम जल्द से जल्द आपसे संपर्क करेंगे।",
        "successTitle": "संदेश सफलतापूर्वक भेज दिया गया!",
        "successMessage": "हमसे संपर्क करने के लिए धन्यवाद। हम 24 घंटे के अंदर आपसे संपर्क करेंगे।",
        "form": {
          "fullName": "पूरा नाम",
          "fullNamePlaceholder": "अपना पूरा नाम दर्ज करें",
          "phoneNumber": "फोन नंबर",
          "phoneNumberNote": "(10 अंक)",
          "phoneNumberPlaceholder": "10 अंकों का फोन नंबर दर्ज करें",
          "emailAddress": "ईमेल पता",
          "emailAddressPlaceholder": "अपना ईमेल पता दर्ज करें",
          "enquiryType": "पूछताछ का प्रकार",
          "message": "संदेश",
          "messagePlaceholder": "बताएं कि हम आपकी कैसे मदद कर सकते हैं...",
          "charactersCount": "अक्षर",
          "sendingMessage": "संदेश भेजा जा रहा है...",
          "sendMessage": "संदेश भेजें"
        },
        "enquiryTypes": {
          "general": "सामान्य",
          "serviceInquiry": "सेवा पूछताछ",
          "technicalSupport": "तकनीकी सहायता",
          "partnership": "भागीदारी",
          "feedback": "फीडबैक",
          "complaint": "शिकायत",
          "other": "अन्य"
        },
        "validation": {
          "nameRequired": "नाम आवश्यक है",
          "nameMinLength": "नाम कम से कम 2 अक्षर का होना चाहिए",
          "nameMaxLength": "नाम 50 अक्षर से कम होना चाहिए",
          "nameOnlyLetters": "नाम में केवल अक्षर और स्पेस हो सकते हैं",
          "emailRequired": "ईमेल आवश्यक है",
          "emailInvalid": "कृपया एक वैध ईमेल पता दर्ज करें",
          "phoneRequired": "फोन नंबर आवश्यक है",
          "phoneExact10Digits": "फोन नंबर ठीक 10 अंकों का होना चाहिए",
          "phoneOnlyDigits": "फोन नंबर में केवल अंक हो सकते हैं",
          "messageRequired": "संदेश आवश्यक है",
          "messageMinLength": "संदेश कम से कम 10 अक्षर का होना चाहिए",
          "messageMaxLength": "संदेश 250 अक्षर से कम होना चाहिए"
        }
      },
      "aboutUs": {
        "pageTitle": "हमारे बारे में",
        "heroTitle": "आपका विश्वसनीय आध्यात्मिक साझीदार",
        "heroSubtitle": "दिलों को दिव्य परंपराओं से जोड़ना",
        "heroDescription": "हम अनुभवी पंडितों और पूर्ण अनुष्ठान व्यवस्था के साथ प्रामाणिक आध्यात्मिक सेवाएं और पारंपरिक समारोह आपके द्वार तक लाते हैं।",
        "ourStoryTitle": "हमारी कहानी",
        "ourStoryText": "आध्यात्मिक परंपराओं के लिए गहरे सम्मान के साथ स्थापित, हम प्राचीन ज्ञान और आधुनिक सुविधा के बीच एक सेतु का काम करते हैं। हमारा मिशन सभी के लिए प्रामाणिक आध्यात्मिक सेवाओं को सुलभ बनाना है।",
        "missionTitle": "हमारा मिशन",
        "missionText": "प्रामाणिक समारोहों, अनुभवी पंडितों और व्यक्तिगत आध्यात्मिक मार्गदर्शन के माध्यम से आध्यात्मिक परंपराओं की समृद्ध विरासत को संरक्षित करना और साझा करना।",
        "trustStatsTitle": "हजारों लोगों का भरोसा",
        "stats": {
          "ceremonies": "1000+ समारोह",
          "families": "500+ खुश परिवार",
          "priests": "50+ विशेषज्ञ पंडित",
          "years": "5+ साल का अनुभव"
        },
        "trustFeaturesTitle": "हमें क्यों चुनें",
        "trustFeatures": {
          "expertPriests": "विशेषज्ञ पंडित",
          "authenticRituals": "प्रामाणिक अनुष्ठान",
          "satisfaction": "संतुष्टि की गारंटी",
          "support": "24/7 सहायता"
        },
        "teamTitle": "हमारी आध्यात्मिक टीम",
        "teamSubtitle": "हमारे अनुभवी पंडितों और आध्यात्मिक गुरुओं से मिलें",
        "servicesTitle": "हमारी सेवाओं में शामिल",
        "cta": "आज ही अपनी आध्यात्मिक सेवा बुक करें"
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false,
    interpolation: {
      escapeValue: false // React already does escaping
    },
    detection: {
      // Store language preference in localStorage
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'selectedLanguage'
    }
  });

export default i18n;